"use client";

import { cn } from "@ui/lib/utils";
import ClassNames from "embla-carousel-class-names";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import * as React from "react";

type SliderApi = UseEmblaCarouselType[1];
type UseSliderParameters = Parameters<typeof useEmblaCarousel>;
export type SliderOptions = UseSliderParameters[0];
export type SliderPlugin = UseSliderParameters[1];

type SliderProps = {
  opts?: SliderOptions;
  orientation?: "horizontal" | "vertical";
  plugins?: SliderPlugin;
  ref?: React.Ref<HTMLDivElement | null>;
  setApi?: (api: SliderApi) => void;
};

type SliderContextProps = {
  api: ReturnType<typeof useEmblaCarousel>[1];
  canScrollNext: boolean;
  canScrollPrev: boolean;
  scrollNext: () => void;
  scrollPrev: () => void;
  sliderRef: ReturnType<typeof useEmblaCarousel>[0];
} & SliderProps;

const SliderContext = React.createContext<SliderContextProps | null>(null);

function useSlider() {
  const context = React.useContext(SliderContext);

  if (!context) {
    throw new Error("useSlider must be used within a <Slider />");
  }

  return context;
}

const Slider = ({
  orientation = "horizontal",
  opts,
  setApi,
  plugins = [],
  className,
  children,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & SliderProps) => {
  const [sliderRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    [...plugins, ClassNames()],
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: SliderApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <SliderContext.Provider
      value={{
        sliderRef,
        api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        aria-roledescription='slider'
        className={cn("relative", className)}
        onKeyDownCapture={handleKeyDown}
        ref={ref}
        role='region'
        {...props}
      >
        {children}
      </div>
    </SliderContext.Provider>
  );
};
Slider.displayName = "Slider";

const SliderContent = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.RefObject<HTMLDivElement> }) => {
  const { sliderRef } = useSlider();

  return (
    <div className={className} ref={sliderRef}>
      <div className={cn("flex")} ref={ref} {...props} />
    </div>
  );
};
SliderContent.displayName = "SliderContent";

const SliderItem = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.RefObject<HTMLDivElement> }) => {
  return (
    <div
      aria-roledescription='slide'
      className={cn("min-w-0 shrink-0 grow-0", className)}
      ref={ref}
      role='group'
      {...props}
    />
  );
};
SliderItem.displayName = "SliderItem";

export { Slider, type SliderApi, SliderContent, SliderItem, useSlider };
